import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Loader2, Truck, CreditCard, Eye, History } from "lucide-react";
import { toast } from "sonner";

const TIPI = {
  ritiro: { label: "Ritiro / consegna", icon: Truck, cls: "bg-sky-600 text-white" },
  pagamento: { label: "Pagamento mancante", icon: CreditCard, cls: "bg-rose-600 text-white" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export const FunnelBadge = ({ funnel }) => {
  if (!funnel || !funnel.attivo) return null;
  const t = TIPI[funnel.tipo] || TIPI.ritiro;
  const Icon = t.icon;
  return (
    <Badge className={`${t.cls} gap-1 text-[10px]`} title={funnel.testo} data-testid="funnel-badge">
      <Icon className="h-3 w-3" />{t.label}
    </Badge>
  );
};

export default function FunnelButton({ numero, tipi, funnel, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState(funnel?.tipo || "ritiro");
  const [testo, setTesto] = useState(funnel?.testo || (tipi && tipi.ritiro) || "");
  const [busy, setBusy] = useState(false);
  const [storico, setStorico] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const active = funnel && funnel.attivo;

  const openDlg = async () => {
    const tp = funnel?.tipo || "ritiro";
    setTipo(tp);
    setTesto(funnel?.testo || (tipi && tipi[tp]) || "");
    setAnteprima(null);
    setStorico(null);
    setOpen(true);
    try {
      const { data } = await api.get(`/commesse/funnel/storico`, { params: { numero } });
      setStorico(data);
    } catch { /* noop */ }
  };
  const pickTipo = (tp) => { setTipo(tp); setTesto((tipi && tipi[tp]) || ""); setAnteprima(null); };

  const loadAnteprima = async () => {
    setLoadingPrev(true);
    try {
      const { data } = await api.get(`/commesse/funnel/anteprima`, { params: { numero, tipo, testo } });
      setAnteprima(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore anteprima");
    } finally { setLoadingPrev(false); }
  };

  const save = async (attivo) => {
    setBusy(true);
    try {
      await api.post("/commesse/funnel", { numero, tipo, testo, attivo });
      toast.success(attivo ? "Funnel attivato" : "Funnel disattivato");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio funnel");
    } finally { setBusy(false); }
  };

  if (!canEdit) return active ? <FunnelBadge funnel={funnel} /> : null;

  const ultimo = storico?.ultimo;

  return (
    <>
      <div className="inline-flex items-center gap-1">
        {active && <FunnelBadge funnel={funnel} />}
        <Button size="sm" variant={active ? "secondary" : "outline"} className="h-6 gap-1 px-2 text-[10px] border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400" onClick={openDlg} data-testid={`funnel-btn-${numero}`}>
          <Megaphone className="h-3 w-3" />Funnel
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="funnel-dialog">
          <DialogHeader><DialogTitle>Funnel commessa {numero}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TIPI).map(([k, t]) => {
                const Icon = t.icon;
                return (
                  <button key={k} type="button" onClick={() => pickTipo(k)} data-testid={`funnel-tipo-${k}`}
                    className={`flex items-center gap-2 rounded-md border p-2 text-left text-xs ${tipo === k ? "border-primary bg-primary/10" : "border-border"}`}>
                    <Icon className="h-4 w-4 text-primary" />{t.label}
                  </button>
                );
              })}
            </div>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">Messaggio (modificabile)</label>
              <Textarea rows={4} value={testo} onChange={(e) => { setTesto(e.target.value); setAnteprima(null); }} className="mt-1 text-xs" data-testid="funnel-testo" />
            </div>

            {/* Storico invii */}
            <div className="rounded-md border border-border bg-muted/30 p-2" data-testid="funnel-storico">
              <div className="mb-1 flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                <History className="h-3 w-3" />Storico invii
              </div>
              {storico === null ? (
                <p className="text-[11px] text-muted-foreground">Caricamento…</p>
              ) : !ultimo ? (
                <p className="text-[11px] text-muted-foreground">Nessun promemoria inviato per questa commessa.</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-[11px]">
                    Ultimo invio: <strong>{fmtDate(ultimo.data)}</strong>
                  </p>
                  <p className="text-[11px] text-muted-foreground break-all">
                    A: {ultimo.to || "—"}{ultimo.cc?.length ? ` · CC: ${ultimo.cc.join(", ")}` : ""}
                  </p>
                  {storico.invii?.length > 1 && (
                    <details className="text-[10px] text-muted-foreground">
                      <summary className="cursor-pointer">Ultimi {storico.invii.length} invii</summary>
                      <ul className="mt-1 space-y-0.5">
                        {storico.invii.map((i, idx) => (
                          <li key={idx}>{fmtDate(i.data)} → {i.to}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>

            {/* Anteprima email */}
            <div>
              <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-[11px]" onClick={loadAnteprima} disabled={loadingPrev} data-testid="funnel-anteprima-btn">
                {loadingPrev ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}Anteprima email
              </Button>
              {anteprima && (
                <div className="mt-2 rounded-md border border-border" data-testid="funnel-anteprima">
                  {anteprima.ok === false ? (
                    <p className="p-2 text-[11px] text-rose-600">{anteprima.motivo}</p>
                  ) : (
                    <>
                      <div className="border-b border-border bg-muted/30 p-2 text-[11px]">
                        <p><span className="text-muted-foreground">Oggetto:</span> <strong>{anteprima.subject}</strong></p>
                        <p className="break-all"><span className="text-muted-foreground">A:</span> {anteprima.to}</p>
                        {anteprima.cc?.length ? <p className="break-all"><span className="text-muted-foreground">CC:</span> {anteprima.cc.join(", ")}</p> : null}
                      </div>
                      <div className="max-h-64 overflow-y-auto bg-white text-black" dangerouslySetInnerHTML={{ __html: anteprima.html }} />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            {active
              ? <Button variant="destructive" size="sm" onClick={() => save(false)} disabled={busy} data-testid="funnel-disattiva">Disattiva funnel</Button>
              : <span />}
            <Button size="sm" onClick={() => save(true)} disabled={busy} data-testid="funnel-salva">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{active ? "Aggiorna" : "Attiva funnel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
